import { Observable } from "rxjs";
import { ApiResponse, PaginablePayloadApiResponse } from "./types.domain";
import { IndividualDirector } from "./component/director-component.domain";
import { Address2ComponentValue } from "./component/address2-component.domain";
import { Application, CommercialEntity, GetApplicationDscrFn, Individual, LocalApplicationDbReloadFn } from "./application-service.domain";
import type { ApplicationCommercialEntity, ApplicationIndividual, GetApplicationPartiesPayload } from "./application-parties.domain";
import { BsaCalculator } from "./component/bank-statements-analysis.domain";
import { GetLvrCalculatorValueFn } from "./component/lvr-calculator-component.domain";
import { GetUserFn } from "./auth-service.domain";
import { GroupedDocument } from "./component/document-details-component.domain";
import { Accreditation } from "./accreditation-service.domain";

export type CreditOverviewSelection = 'entity' | 'director' | 'guarantor' | 'summary' | 'result';

export const CreditWebServiceOptions = [{
    name: 'CreditorWatch',
    id: 1
  }, {
    name: 'EquifaxConsumer',
    id: 2
  },{
    name: 'EquifaxFraudCheck',
    id: 3
  }]

export interface CreditFlow {
    id: number;
    name: string;
    startNode: number;
    isDeleted: boolean;
    lastUpdatedBy: number;
    lastUpdatedTime: string;
    BusinessLoans: boolean;
    BusinessOverdraft: boolean;
    InsurancePremium: boolean;
    CorporateLoans: boolean;
    Consumer: boolean;
    Accreditation: boolean;
    AssetLowDoc: boolean;
    AssetEasyDoc: boolean;
    AssetFullDoc: boolean;
    GivenName?: string;
    FamilyName?: string;
    published: boolean;
    ConnectToOtherCreditFlowCondition?: number; // credit rule item id
    ConnectedCreditFlowId?: number;
    ConnectToOtherCreditFlowStatus: CreditAlertStatus;
}

export interface UpdateCreditFlowBody extends Partial<CreditFlow> {
    force?: boolean;
}
// use dfs/bfs algorithm to display the graph

export interface CreditFlowNode {
    id: number;
    creditFlowId: number; // foreign key
    name: string;
    isDeleted: boolean;
    lastUpdatedBy: number;
    lastUpdatedTime: string;
    type: CreditFlowNodeType,
    webService: WebServiceType | null; // applied only if this is a web service node
    nextNode: number | null; // applied only if this is a web service node
    webServiceLevel: CreditRuleMappingLevel | null;
    conditionVariable: number | null;
}

export interface CreditRuleMapping {
    id: number;
    creditFlowNodeId: number; // foreign key
    name: string;
    isDeleted: boolean;
    lastUpdatedBy: number;
    lastUpdatedTime: string;
    level: CreditRuleMappingLevel;
    creditRule: string; // rule formula
    alertId: number; //CreditAlert;
    conditionVariable: number | null;
    action: CreditRuleMappingAction | null;
    nextNode: number | null; // router; null means that's the end of the route
    FamilyName?: string;
    GivenName?: string;
    // queue/status??
}

export type CreditRuleMappingLevel = 'Individual' | 'Entity' | 'Application' | 'Accreditation';

export interface CreditRuleMappingBody {
    name: string;
    creditFlowNodeId: number;
    level: CreditRuleMappingLevel;
    creditRule: string;
    alertId: number;
    nextNode: number | null;
    conditionVariable: number | null;
    action: CreditRuleMappingAction | null;
}

export interface CreditFlowNodeBody {
    name: string;
    type: CreditFlowNodeType,
    webService: WebServiceType | null; // applied only if this is a web service node
    creditFlowId: number;
    nextNode: number | null;
    webServiceLevel: CreditRuleMappingLevel | null;
    conditionVariable: number | null;
}

export type CreditFlowNodeType = 'web service' | 'rule set' | 'route' | 'end';
export type CreditAlertStatus = 'Decline' | 'Refer' | 'Validate' | 'Conditional';

export const CreditAlertOptions = ['Decline', 'Refer', 'Validate', 'Conditional'];

export interface CreditAlert {
    id: number;
    name: string;
    isDeleted: boolean;
    lastUpdatedBy: number;
    lastUpdatedTime: string;
    status: CreditAlertStatus;
    description: string;
    FamilyName?: string;
    GivenName?: string;
    code: string;
    condition: string;
    conditionDescription: string;
}

export type DuplicatedApplication = Application & {
    duplicatedReason: string;
}

export interface ApplicationCreditFlow {
    id: number;
    applicationId: number;
    creditFlowId: number;
    creditFlowName: string;
    flow: ApplicationCreditFlowNodeDetail[];
    status: string;
    lastUpdatedTime: string;
    completeFlow: boolean;
    creditFlowNodeName?: string;
}

export interface AccreditationCreditFlow {
    id: number;
    accreditationId: number;
    creditFlowId: number;
    creditFlowName: string;
    flow: ApplicationCreditFlowNodeDetail[];
    status: string;
    lastUpdatedTime: string;
    completeFlow: boolean;
    creditFlowNodeName?: string;
}

export interface ApplicationCreditFlowNodeDetail {
    creditFlowId: number;
    creditFlowNode: number;
    creditFlowNodeName: string;
    calculation: string; // audit the calculation history
    ruleMappingId: number | null;
    ruleMappingName: string;
    alertId: number | null;
    alert: string;
    alertCode: string;
    alertStatus: CreditAlertStatus | null | 'FYI';
    abn: string | null;
    acn: string | null;
    individualId: string | null;
    creditFlowName?: string;
    // Set on the marker row of a run that found a human credit officer already assigned (AZ-1711).
    creditOfficerAssignedRerun?: boolean;
}

export interface ApplicationCreditFlowNodeDetailTableData extends ApplicationCreditFlowNodeDetail {
    lastUpdatedTime: string;
    runId: number;
    applicant: string;
}

export interface AccreditationCreditFlowNodeDetailTableData extends ApplicationCreditFlowNodeDetail {
    lastUpdatedTime: string;
    runId: number;
}

export interface ApplicationCreditFlowBody {
    applicationId: number;
    creditFlowId: number;
    flow: string;
    status: string;
    completeFlow: boolean;
}

export interface ApplicationCreditFlowToCreditReportBody {
    applicationCreditFlowId: number;
    creditReportId: number;
}

export interface AccreditationCreditFlowToCreditReportBody {
    accreditationCreditFlowId: number;
    creditReportId: number;
}

export interface AccreditationCreditFlowBody {
    accreditationId: number;
    creditFlowId: number;
    flow: string;
    status: string;
    completeFlow: boolean;
}

export interface NewCreditFlowBody {
    name: string;
    AssetLowDoc?: boolean;
    AssetEasyDoc?: boolean;
    AssetFullDoc?: boolean;
    BusinessLoans?: boolean;
    BusinessOverdraft?: boolean;
    InsurancePremium?: boolean;
    CorporateLoans?: boolean;
    Consumer?: boolean;
    Accreditation?: boolean;
    ConnectToOtherCreditFlowCondition?: number;
    ConnectedCreditFlowId?: number;
    ConnectToOtherCreditFlowStatus?: string;
}

export interface TestCreditFlowBody {
    applicationId: number;
    creditFlowId: number;
}

export interface TestCreditFlowNodeBody {
    applicationId: number;
    creditFlowNodeId: number;
    creditFlowId: number
}

export interface TestCreditRuleBody {
    applicationId: number;
    creditRuleMapping: CreditRuleMapping;
    creditFlowNodeId: number;
    creditFlowId: number
}

export interface TestCreditRuleItemBody {
    applicationId: number;
    creditRuleItem: CreditRuleItem;
}

export interface TestCreditFlowResponse {
    flow: ApplicationCreditFlowNodeDetail[];
    status: string;
    application: Application | null;
}

export type AddCreditFlowFn = (data: NewCreditFlowBody) => Observable<number>;
export type GetCreditFlowsFn = () => Observable<CreditFlow[]>;
export type UpdateCreditFlowFn = (data: Partial<CreditFlow>, id: number) => Observable<ApiResponse>;
export type CopyCreditFlowFn = (data: CopyCreditFlowBody) => Observable<ApiResponse>;
export type GetCreditWebServicesFn = (applicationId: number, isSeeMore?: boolean) => Observable<NewWebService[]>;
export type GetApplicationCreditFlowResultFn = (applicationId: number) => Observable<ApplicationCreditFlow[]>;
export type GetWebServiceReportFn = (webServiceId: number) => Observable<GetWebServiceReportResponse>;
export type GetDuplicatedApplicationsFn = (applicationId: number) => Observable<DuplicatedApplication[]>;
export type GetAccreditationCreditFlowResultFn = (accreditationId: number) => Observable<AccreditationCreditFlow[]>;
export type GetAccreditationByIdFn = (accreditationId: number) => Observable<Accreditation>;
export type GetWebServiceReportResponse = GetWebServiceReportSuccessResponse | null;
export type GetAccreditationReportFn = (webServiceId: number) => Observable<ApiResponse>;

export interface HistoricalContract {
  EntityName?: string | null;
  ABN?: string | null;
  ACN?: string | null;
  FiscalContractCode?: string | null;
  tenure?: number | null;
  arrears60dplus?: number | null;
  WDV?: number | null;
  WOFF_flag?: boolean | null;
  WorstArrearsL12m?: string | null;
  DishonourCount?: number | null;
  DishonourCountL12m?: number | null;
}

export type GetHistoricalContractsFn = (applicationId: number) => Observable<HistoricalContract[]>;

export interface PismoContract {
  accountName: string | null;
  pismoAccountNumber: number | null;
  totalLimit: number | null;
  currentBalance: number | null;
  currentUtilisationPct: number | null;
  status: string | null;
  amortisation: boolean;
  creationDate: string | null;
  dishonourCountL12m: number | null;
}

export type GetPismoContractsFn = (applicationId: number) => Observable<PismoContract[]>;

export interface GetWebServiceReportSuccessResponse {
    webService: ApplicationWebService,
    data: string | null
}

export interface EditCreditFlowDialogData {
    name: string;
}

export interface EditCreditFlowDialogResult {
    name: string;
    readyForSubmission: boolean;
}
export interface PublishCreditFlowConfirmationDialogData {
    conflictedFlows: ConflictedFlowDetail[]
}

export interface PublishCreditFlowConfirmationDialogResult {
    force: boolean;
}

export interface EditCreditRuleMappingDialogData {
    creditRuleMapping: CreditRuleMapping | null;
    creditFlowNodeId: number;
    creditFlowId: number;
    nextNodeList: CreditFlowNode[];
    creditAlerts: CreditAlert[];
    nodeType: CreditFlowNodeType;
    getCreditRuleItemsFn: GetCreditRuleItemsFn;
    testCreditRuleFn: TestCreditRuleFn;
    testCreditFlowFn: TestCreditFlowFn;
    testCreditFlowNodeFn: TestCreditFlowNodeFn;
    testCreditRuleItemFn: TestCreditRuleItemFn;
    localApplicationDbReloadFn: LocalApplicationDbReloadFn;
    readOnly?: boolean;
}

export interface EditCreditRuleMappingDialogResult {
    readyForSubmission: boolean;
    creditRuleMapping: CreditRuleMappingBody | null;
}

export interface EditCreditFlowNodeDialogData {
    creditFlowNode: CreditFlowNode | null;
    creditFlowId: number;
    nextNodeList: CreditFlowNode[];
    getCreditRuleItemsFn: GetCreditRuleItemsFn;
    testCreditRuleFn: TestCreditRuleFn;
    testCreditFlowFn: TestCreditFlowFn;
    testCreditFlowNodeFn: TestCreditFlowNodeFn;
    testCreditRuleItemFn: TestCreditRuleItemFn;
    localApplicationDbReloadFn: LocalApplicationDbReloadFn;
    readOnly?: boolean;
}

export interface EditCreditFlowNodeDialogResult {
    readyForSubmission: boolean;
    creditFlowNode: CreditFlowNodeBody | null;
}

export interface ConnectToOtherCreditFlowDialogData {
    condition: number | null;
    creditFlowId: number | null;
    status: CreditAlertStatus | null;
    currentFlowId: number;
    getCreditRuleItemsFn: GetCreditRuleItemsFn;
    getCreditFlowsFn: GetCreditFlowsFn;
}

export interface ConnectToOtherCreditFlowDialogResult {
    readyForSubmission: boolean;
    clearConnection: boolean;
    condition: number | null;
    creditFlowId: number | null;
    status: CreditAlertStatus | null;
}

export interface CreditFlowDetails extends CreditFlow {
    nodes: CreditFlowNodeDetails[];
}

export interface CreditFlowNodeDetails extends CreditFlowNode {
    creditRuleMappings: CreditRuleMapping[];
}

export interface CreditFlowNodeWithLevel extends CreditFlowNodeDetails {
    level: number;
}

export interface NewCreditAlertBody {
    name: string;
    description: string;
    status: CreditAlertStatus;
    code: string;
    condition: string;
    conditionDescription: string;
}

export type RunCreditFlowFn = (
    applicationId: number,
    startFromLastRun: boolean,
    productType?: 'FAST DOC' | 'EASY DOC' | 'FULL DOC'
) => Observable<ApiResponse>;
export type RunAccreditationCreditFlowFn = (accreditationId: number, startFromLastRun: boolean) => Observable<ApiResponse>;
export type AddCreditAlertFn = (data: NewCreditAlertBody) => Observable<number>;
export type GetCreditAlertsFn = () => Observable<CreditAlert[]>;
export type UpdateCreditAlertFn = (data: Partial<CreditAlert>, id: number) => Observable<ApiResponse>;

export interface EditCreditAlertDialogData {
    name: string;
    description: string;
    code: string;
    status: CreditAlertStatus | null;
    condition: string;
    conditionDescription: string;
    getCreditAlertByStatusFn: GetCreditAlertByStatusFn;
}

export interface EditCreditAlertDialogResult {
    name: string;
    description: string;
    code: string;
    status: CreditAlertStatus;
    condition: string;
    conditionDescription: string;
    readyForSubmission: boolean;
}

export interface CopyCreditFlowBody {
    flowId: number;
}

export interface DeleteWebServicesBody {
    webServiceIds: number[];
}

export type DeleteWebServicesFn = (webServiceIds: number[]) => Observable<ApiResponse>

export type CreditRuleComponentValue = string | null;

export type CreditRuleOperator = "(" | ")" | "!=" | "==" | ">=" | "<=" | ">" | "<" | "+" | "-" | "*" | "/" | "DOT" |"IN" | "NOT IN" | "AND" | "OR";

export interface CreditRuleObject {
    type: 'variable' | 'operator' | 'value',
    value: string | number | boolean | (string | number | boolean)[];
}

export type CreditRuleItemType = 'Database' | 'JSON' | 'Formula' | 'Finesse Database';

export type CreditRuleItemFunction = 
    'None' |  
    'Count' | 
    'First' | 
    'Last' |  
    'Uppercase' |
    'Lowercase' |
    'Age' |
    'Number' |  
    'Round' |  
    'Round up' |  
    'Round down' |  
    'Sum' |  
    'Maximum' |  
    'Minimum' |  
    'Average' | 
    'DB Value' |
    'Contains Deregistered' |
    'Max Age' |
    'Min Age' |
    'Stringify';
// json only
export type CreditRuleItemLevel = 'Application' | 'Entity' | 'Individual' | 'Accreditation';

export type CreditRuleMappingAction = 'Send Bank Statements Request';


export type CreditRuleItemOutputType = 'Date' | 'Date Time' | 'Int' | 'Float' | 'Text' | 'Boolean' | 'Array' | 'Object';

export const CreditRuleItemTypeOptions = ['JSON', 'Formula', 'Database', 'Finesse Database'];
export const CreditRuleItemFunctionOptions = [
    'None',  
    'Count', 
    'First', 
    'Last', 
    'Uppercase',
    'Lowercase',
    'Age',
    'Number', 
    'Round', 
    'Round up', 
    'Round down', 
    'Sum', 
    'Maximum',
    'Minimum',
    'Average',
    'DB Value',
    'Contains Deregistered',
    'Max Age',
    'Min Age',
];
export const CreditRuleItemLevelOptions = ['Application', 'Entity', 'Individual', 'Accreditation'];
export const CreditRuleItemOutputTypeOptions = ['Date', 'Date Time', 'Int', 'Float', 'Text', 'Boolean', 'Array', 'Object'];
export const CreditRuleMappingActionOptions = ['Send Bank Statements Request'];
export interface CreditRuleItem extends NewCreditRuleItemBody {
    id: number;
    isDeleted: boolean;
    lastUpdatedBy: number;
    lastUpdatedTime: string;
}

export interface NewCreditRuleItemBody {
    name: string;
    description: string;
    type: CreditRuleItemType;
    useDefaultValue: boolean;
    defaultValue: string;
    itemFunction: CreditRuleItemFunction;
    level: CreditRuleItemLevel | null;
    outputType: CreditRuleItemOutputType;
    value: string;
}

export interface EditCreditRuleItemDialogData {
    creditRuleItem: CreditRuleItem | null;
    getCreditRuleItemsFn: GetCreditRuleItemsFn;
    testCreditRuleFn: TestCreditRuleFn;
    testCreditFlowFn: TestCreditFlowFn;
    testCreditFlowNodeFn: TestCreditFlowNodeFn;
    testCreditRuleItemFn: TestCreditRuleItemFn;
    localApplicationDbReloadFn: LocalApplicationDbReloadFn;
    readOnly?: boolean;
}

export interface EditCreditRuleItemDialogResult {
    readyForSubmission: boolean;
    creditRuleItem: NewCreditRuleItemBody | null;
}
export interface ConflictedFlowDetail { conflictingId: number; conflictingFlowName: string; product: (keyof CreditFlow)[]; };

export interface PublishCreditFlowBody {
    force: boolean;
}
export type AddCreditRuleItemFn = (data: NewCreditRuleItemBody) => Observable<number>;
export type GetCreditRuleItemsFn = () => Observable<CreditRuleItem[]>;
export type UpdateCreditRuleItemFn = (data: Partial<CreditRuleItem>, id: number) => Observable<ApiResponse>;
export type PublishCreditFlowFn = (data: Partial<PublishCreditFlowBody>, id: number) => Observable<ApiResponse>;
export type DownloadCreditorWatchReportFn = (pdfId: number) => Observable<any>;
export type UpdateCreditStatusFn = (data: UpdateCreditStatusBody) => Observable<ApiResponse>;
export type UpdateCreditOfficerFn = (data: UpdateCreditOfficerBody) => Observable<ApiResponse>;
export type GetCreditAuditLogFn = (applicationId: number, opts: {
    page: { limit: number, offset: number },
    filter: string,
  }) => Observable<PaginablePayloadApiResponse<CreditAuditLog>>;
export type GetCreditAlertByStatusFn = (status: CreditAlertStatus) => Observable<number>;
export type GetAccreditationWebServicesFn = (accreditationId: number) => Observable<AccreditationWebService[]>;
export type ApproveAccreditationFn = (data: ApproveAccreditationBody) => Observable<ApiResponse>;
export type MarkAccreditationAccreditedFn = (data: MarkAccreditationAccreditedBody) => Observable<ApiResponse>;
export type DeclineAccreditationFn = (data: DeclineAccreditationBody) => Observable<ApiResponse>;
export type TestCreditFlowFn = (data: TestCreditFlowBody) => Observable<TestCreditFlowResponse>;
export type TestCreditFlowNodeFn = (data: TestCreditFlowNodeBody) => Observable<TestCreditFlowResponse>;
export type TestCreditRuleFn = (data: TestCreditRuleBody) => Observable<TestCreditFlowResponse>;
export type TestCreditRuleItemFn = (data: TestCreditRuleItemBody) => Observable<TestCreditFlowResponse>;

export interface TestCreditFlowDialogData {
    testCreditFlowFn: TestCreditFlowFn;
    testCreditFlowNodeFn: TestCreditFlowNodeFn;
    testCreditRuleFn: TestCreditRuleFn;
    testCreditRuleItemFn: TestCreditRuleItemFn;
    localApplicationDbReloadFn: LocalApplicationDbReloadFn;
    type: 'credit-flow' | 'credit-flow-node' | 'credit-rule' | 'credit-rule-item';
    creditFlowId: number;
    creditFlowNodeId: number | null;
    creditRuleMapping: CreditRuleMapping | null;
    creditRuleItem: CreditRuleItem | null;
}

export interface TestCreditFlowDialogResult {

}

export interface CreditRuleItemSelections {
    [propertyName: string]: CreditRuleItem[]
}

export type WebServiceType = 'CreditorWatch' | 'EquifaxConsumer' | 'EquifaxFraudCheck' | 'ComputeAssociatedEntities';

// ---------------------------------------------------------------------------
// The credit-rule input contract (AZ-1475 Phase 3).
//
// JSON-type CreditRuleItem rows hold JavaScript path expressions that are eval'd against these
// objects, and they are editable in the credit management screen with no validation on save. So the
// field names below cannot be renamed with a compiler's help, and a broken path does not throw - the
// evaluator catches, logs, and returns the rule's default value. Renaming silently changes credit
// decisions.
//
// Treat every name reachable from here as public. To change one, migrate the stored expressions in
// the same deployment, or intercept the old path in evaluateJSONCreditRuleItem the way
// CreditRuleItem 116 is handled after Individual.PrivacyConsent was dropped.
//
// Deliberately a superset of Application rather than an allowlist: production rule text cannot be
// enumerated from source, so narrowing the reachable surface risks silently defaulting a live rule.
// ---------------------------------------------------------------------------

/**
 * Bureau reports as they are grafted onto a party by getApplicationWithWebServices, keyed by the
 * ApplicationWebService.type that produced them.
 */
export interface WebServiceReportsByType {
    CreditorWatch?: CreditorWatchReportData;
    EquifaxConsumer?: EquifaxConsumerReportData;
    EquifaxFraudCheck?: EquifaxFraudCheckReportData;
    ComputeAssociatedEntities?: unknown;
}

/** An entity with its entity-level bureau reports attached. Matched to reports by abn/acn. */
export type CreditRuleEntity = ApplicationCommercialEntity & WebServiceReportsByType;

/**
 * An individual with their individual-level bureau reports attached.
 *
 * Note that `surname`, `givenName` and `dateOfBirth` are load-bearing beyond any rule expression
 * that reads them: the graft matches reports to people on those three fields, so renaming one
 * attaches no report at all and every rule reading `EquifaxConsumer` silently falls back to its
 * default.
 */
export type CreditRuleIndividual = ApplicationIndividual & WebServiceReportsByType;

/**
 * What a JSON credit rule item at level 'Application' is evaluated against.
 *
 * Not simply a hydrated Application: `individuals` has already been filtered to consenting guarantor
 * signatories, and the three extra members below exist only so rules can read them.
 *
 * **`individuals` is not `parties.individuals`.** The hydrator attaches the full party set under
 * `parties`; the arrays here are the narrowed, bureau-grafted set. A rule expression reaching for
 * `['parties']['individuals']` would see a different, wider population and no bureau reports.
 */
export type CreditRuleInput = Application & {
    // An intersection, not Omit-and-redeclare. Application is a six-member discriminated union, so
    // Omit collapses it to one object type and drops the per-product fields (ApplicationLocation,
    // PreviousBusiness), leaving the result unassignable back to Application.
    //
    // Since AZ-1475 removed Individuals/CommercialEntities from the read types these two members no
    // longer collide with anything on Application, so unlike the legacy pair they narrow properly
    // and chained array methods see the report fields without an annotation. Enforcement against a
    // rename still comes from CREDIT_RULE_RESERVED_* in credit-party-rule-guard.ts, which fails to
    // compile, because the stored expressions cannot follow one.
    individuals: CreditRuleIndividual[];
    commercialEntities: CreditRuleEntity[];
    /** Bank statement analysis, when one has been run. */
    BankStatements?: BsaCalculator;
    /** Salesforce Account flags that are not persisted on our side. */
    SalesforceAccount?: { HardshipClaim: boolean };
};

export interface ApplicationWebService {
    id: number,
    applicationId: number,
    level: 'entity' | 'individual',
    ABN: string | null,
    ACN: string | null,
    individualId: string | null,
    type: WebServiceType,
    json: Object,
    lastUpdatedTime: string,
    isDeleted: boolean,
}

export interface AccreditationWebService {
    id: number,
    accreditationId: number,
    ABN: string | null,
    ACN: string | null,
    type: WebServiceType,
    json: Object,
    lastUpdatedTime: string,
    isDeleted: boolean,
}

export interface NewApplicationWebService {
    applicationId: number,
    level: 'entity' | 'individual',
    ABN: string | null,
    ACN: string | null,
    individualId: string | null,
    type: WebServiceType,
    json: string,
}

export interface NewWebService {
    id?: number,
    level: 'entity' | 'individual',
    ABN: string | null,
    ACN: string | null,
    individualFirstName: string | null,
    individualLastName: string | null,
    individualDoB: string | null,
    // Equifax-only enquiry inputs, part of the cache key for those products so that editing either
    // invalidates the report on file. Left unset by the other web services.
    individualMiddleName?: string | null,
    individualIdentificationNumber?: string | null,
    type: WebServiceType,
    json: string,
    lastUpdatedTime: string,
}

export const WEB_SERVICE_STALE_AFTER_DAYS = 90;

export const webServiceAgeInDays = (lastUpdatedTime: string): number | null => {
    const time = Date.parse(lastUpdatedTime);
    if (isNaN(time)) {
        return null;
    }
    return Math.floor((Date.now() - time) / (24 * 60 * 60 * 1000));
}

export const isWebServiceStale = (lastUpdatedTime: string): boolean => {
    const age = webServiceAgeInDays(lastUpdatedTime);
    return age !== null && age > WEB_SERVICE_STALE_AFTER_DAYS;
}

export interface NewAccreditationWebService {
    accreditationId: number,
    ABN: string | null,
    ACN: string | null,
    type: WebServiceType,
    json: string,
}

export interface CreditorWatchLoginResponse {
    token: string;
    refresh_token: string;
}

export interface SelectWebServicesDialogData {
    webServices: NewWebService[];
    application: Application;
    /** Resolved by the opener, which has already fetched it: a dialog constructor cannot await. */
    parties?: GetApplicationPartiesPayload | null;
    productType?: 'LOW DOC' | 'EASY DOC' | 'FULL DOC';
}

export interface SelectWebServicesDialogResult {
    readyForSubmission: boolean;
    webServiceIds: number[];
    productType?: 'LOW DOC' | 'EASY DOC' | 'FULL DOC';
}

export interface CreditDecisioningReasonDialogData {
    status: CreditOfficerStatus;
}

export interface CreditDecisioningReasonDialogResult {
    readyForSubmission: boolean;
    reasonSelection: string;
    reason: string;
}

export interface CreditDeclineDialogData {
}

export interface CreditDeclineDialogResult {
    readyForSubmission: boolean;
    reasonSelection: string;
    reason: string;
    category: 'Credit Declined' | 'Withdrawn';
}

export interface CreditApprovedDialogData {
    application: Application;
    getApplicationDscrFn: GetApplicationDscrFn;
    getLvrCalculatorValueFn?: GetLvrCalculatorValueFn;
}

export interface CreditApprovedDialogResult {
    creditApprovalDate: string,
    exceptionToPolicy: 'Yes' | 'No',
    creditReference: CreditReference,
    dealStatus: DealStatus,
    financials: 'Yes' | 'No',
    valuationSource: ValuationSource,
    bankStatements: 'Yes' | 'No',
    productType: ProductType,
    approvalConditions: string,
    lvr: number | null,
    dscr: number,
}

export const creditDeclineApprovalReasonOptions: string[] = [
    "Poor cashflow - dishonours/debt collection/other",
    "Servicing not evident",
    "Income not supportive of amount sought",
    "Credit score below minimum",
    "GST or ABN age below minimum",
    "Evidence of poor conduct on current finance facilities",
    "Borrower or borrowing entity adverse",
    "Related or previous entity adverse",
    "Restricted Asset",
    "Property ownership requirements not met",
    "Credit criteria not met",
    "System"
]

export const creditDeclineReasonOptions: string[] = [
    "Poor cashflow - dishonours/debt collection/other",
    "Servicing not evident",
    "Income not supportive of amount sought",
    "Credit score below minimum",
    "GST or ABN age below minimum",
    "Evidence of poor conduct on current finance facilities",
    "Borrower or borrowing entity adverse",
    "Related or previous entity adverse",
    "Restricted Asset",
    "Asset not appropriate for the business operations.",
    "Property ownership requirements not met",
    "Credit criteria not met",
    "System"
];

/**
 * The "Credit Declined" values Salesforce accepts for ec1_CreditReasoning__c on the Accreditation
 * Opportunity record type (0127F000001CfIeQAK). The field is a restricted picklist dependent on
 * LossReason__c, so any other value is rejected; a "Withdrawn" decline takes
 * `withdrawApplicationReasonOptions`, which match that record type's Withdrawn values.
 */
export const accreditationDeclineReasonOptions: { value: string, label: string }[] = [
    { value: "Adverse Credit – Borrower, Guarantor, Related", label: "Adverse Credit – Borrower or other Related Entities" },
    { value: "Character", label: "Character" },
    { value: "Credit Score Below Minimal", label: "Credit Score Below Minimal" },
    { value: "Criminal activity", label: "Criminal activity" },
    { value: "Equipment Restricted", label: "Equipment Restricted" },
    { value: "Fraudulent", label: "Compliance" },
    { value: "Highly Geared", label: "Highly Geared" },
    { value: "Missing Information", label: "Missing Information" },
    { value: "No Property Ownership", label: "No Property Ownership" },
    { value: "Poor Cash flow", label: "Poor Cash flow" },
    { value: "Restricted Industry", label: "Restricted Industry" },
    { value: "Serviceability", label: "Serviceability" },
    { value: "System", label: "System" },
    { value: "Trading Time Below Minimal", label: "Trading Time Below Minimal" },
    { value: "Evidence of poor conduct on current finance facilities", label: "Evidence of poor conduct on current finance facilities" },
];

export type CreditOfficerStatus = 'Approved' | 'Declined' | 'More Information' | 'Request Bank Statements';

export type SystemCreditStatus = 'Approved' | 'Declined' | 'Refer' | 'Validate';

// Dispatch state of a signatory's current privacy consent request, used to decide the
// Send vs Resend affordance in the credit-flow consent-required popup.
export type ConsentBlockDispatchState = 'none' | 'pending' | 'expired' | 'declined' | 'failed';

// One signatory whose privacy consent is not valid (not signed, pending, expired, declined or
// dispatch-failed) at the time the credit flow is evaluated.
export interface CreditFlowConsentBlockIndividual {
    individualId: string;
    name: string;
    reason: 'not-signed' | 'expired';
    consentStatusLabel: string;
    dispatchState: ConsentBlockDispatchState;
    canResend: boolean;
}

// Returned as the payload of runCreditFlow when the flow is blocked because one or more guarantor
// signatories lack a valid privacy consent. The manual (Credit tab) trigger renders this as a popup;
// the programmatic (cron) trigger defers the application to "More Information".
export interface CreditFlowConsentBlockPayload {
    consentBlocked: true;
    individuals: CreditFlowConsentBlockIndividual[];
}

// Readiness of the manual Credit "Approve" button. KYB pass is reported for the warning flow but is
// not a hard prerequisite; unresolved KYB requires an audited acknowledgement at approval time.
// Applications submitted before KYB go-live skip the all-KYC-passed gate (ADO 1626).
export interface CreditApprovalReadiness {
    allKycPassed: boolean;
    kybPassed: boolean;
    creditFlowRun: boolean;
    ready: boolean;
    preKybGoLiveCutoff: boolean;
}

export interface UpdateCreditStatusBody {
    applicationId: number;
    status: CreditOfficerStatus;
    kybRiskAcknowledged?: boolean;
    declineDetails?: {
        reasonSelection: string;
        reason: string;
    },
    approveDetails?: {
        creditApprovalDate?: string,
        exceptionToPolicy?: 'Yes' | 'No',
        creditReference?: CreditReference,
        dealStatus?: DealStatus,
        financials?: 'Yes' | 'No',
        valuationSource?: ValuationSource,
        bankStatements?: 'Yes' | 'No',
        productType: ProductType,
        approvalConditions?: string,
        lvr?: number | null,
        dscr?: number,
    }
}

export interface UpdateCreditOfficerBody {
    applicationId: number;
    creditOfficerId: number;
}

export interface MarkAccreditationAccreditedBody {
  accreditationId: number,
}

export interface ApproveAccreditationBody {
    accreditationId: number;
    creditApprovalDate: string;
    creditApprovalTime: string;
}

export interface DeclineAccreditationBody {
    accreditationId: number;
    reasonSelection: string;
    reason: string;
    category: "Withdrawn" | "Credit Declined";
    closedLostTime: string;
}

export interface ConnectToOtherCreditFlowBody {
    condition: number;
    creditFlowId: number;
    status: CreditAlertStatus;
    currentFlowId: number;
}

export interface ClearConnectToOtherCreditFlowBody {
    creditFlowId: number;
}

export type CreditReference = 'No Dishonors' | 'Deferrals' | 'NA';

export type DealStatus = 
    '1. Full Compliance' |
    '2. Full Compliance - Large Institution, Hospital, Club or Charity' | 
    '3. Full Compliance - Business Continuity' |
    '4. Exception - ABN Age' |
    '5. Exception - DSCR' |
    '6. Exception - Credit Score' |
    '7. Exception - Asset/EOT Age' |
    '8. Exception - Other'

export type ValuationSource = 'Internal Desktop' | 'Redbook' | 'Valuation' | 'Not required';

export type ProductType = 
    'FAST DOC' | 
    'EASY DOC' |
    'FULL DOC' |
    'NEAR PRIME' |
    'BUSINESS LOAN' |
    'SUB PRIME' |
    'INSURANCE PREMIUM FUNDING' |
    'Consumer' |
    'Corporate Loan' |
    'Overdraft'

export const productTypeOptions = [
    'FAST DOC', 
    'EASY DOC',
    'FULL DOC',
    'NEAR PRIME',
    'BUSINESS LOAN',
    'SUB PRIME',
    'INSURANCE PREMIUM FUNDING',
    'Consumer',
    'Corporate Loan',
    'Overdraft'
]

export const creditReferenceOptions = ['No Dishonors', 'Deferrals', 'NA'];
export const dealStatusOptions = [
    '1. Full Compliance',
    '2. Full Compliance - Large Institution, Hospital, Club or Charity',
    '3. Full Compliance - Business Continuity',
    '4. Exception - ABN Age',
    '5. Exception - DSCR',
    '6. Exception - Credit Score',
    '7. Exception - Asset/EOT Age',
    '8. Exception - Other'
]
export const valuationSourceOptions = ['Internal Desktop', 'Redbook', 'Valuation', 'Not required'];

// Full Compliance deal statuses (1-3)
export const FULL_COMPLIANCE_STATUSES: DealStatus[] = [
    '1. Full Compliance',
    '2. Full Compliance - Large Institution, Hospital, Club or Charity',
    '3. Full Compliance - Business Continuity'
];

// Exception deal statuses (4-8)
export const EXCEPTION_STATUSES: DealStatus[] = [
    '4. Exception - ABN Age',
    '5. Exception - DSCR',
    '6. Exception - Credit Score',
    '7. Exception - Asset/EOT Age',
    '8. Exception - Other'
];

// Asset Finance product options only
export const ASSET_FINANCE_PRODUCT_OPTIONS: ProductType[] = ['FAST DOC', 'EASY DOC', 'FULL DOC'];

export interface SelectCreditRuleItemDialogData {
    getCreditRuleItemsFn: GetCreditRuleItemsFn;
}

export interface SelectCreditRuleItemDialogResult {
    readyForSubmission: boolean;
    creditRuleItem: CreditRuleItem;
}

export interface RequestMoreInformationBody {
    additionalInformation: string,
    emailList: string[],
    applicationId: number,
}

export type RequestMoreInformationFn = (body: RequestMoreInformationBody) => Observable<ApiResponse>;

export type CreditAuditLogContext = 'updateCreditStatus' | 'updateAlertStatus' | 'runCreditFlow' | 'updateCreditFlow' | 'updateAnzsic' | 'updateLoanPurpose';

export interface CreditAuditLog {
    id: number,
    applicationId: number,
    dateGenerated: string,
    context: CreditAuditLogContext,
    message: string,
    user: {
        userId: number,
        email: string,
        name: string,
    }
}

export interface PrefillDirectorsDialogData {
    directors: IndividualDirector[];
}

export interface PrefillDirectorsDialogResult {
    readyForSubmission: boolean;
    selectedDirectors: IndividualDirector[];
}

export interface MoreInformationDialogData {
    getUserFn: GetUserFn;
    groupNames:GroupedDocument[];
    application: Application;
    isShowRequestReviewSection: Boolean;
    
}

export interface MoreInformationDialogResult {
    readyForSubmission: boolean,
    additionalInformation: string,
    isInternalOnly: boolean,
    isCreditSelect: boolean,
    isSalesSelect: boolean,
    isSettlementsSelect: boolean,
    isSubmitterSelect: boolean,
    emailList: string[],
}

export interface WebServiceJsonDialogData {
    webService: ApplicationWebService | AccreditationWebService
}

export interface WebServiceJsonDialogResult {
    
}

export interface CreditorWatchReportDialogData {
    // The dialog only reads `json`, which the credit table holds as a string and the accreditation
    // table as a parsed object.
    webService: ApplicationWebService | AccreditationWebService | NewWebService
}

// The report dialog is read-only; closing it returns nothing.
export type CreditorWatchReportDialogResult = void;

export interface CreditorWatchReadableSummary {
    organisationName: string | null;
    australianBusinessNumber: string | null;
    organisationNumber: string | null;
    currentCreditScore: number | null;
    currentCreditRating: string | null;
}

export interface CreditorWatchReportAdverseField {
    label: string;
    value: string;
}

export interface CreditorWatchReportAdverseItem {
    // Single-line summary of the item, so the full field list only has to be opened on demand.
    headline: string;
    fields: CreditorWatchReportAdverseField[];
}

export interface CreditorWatchReportAdverseGroup {
    title: string;
    shortLabel: string;
    items: CreditorWatchReportAdverseItem[];
}

export interface CreditorWatchReportAdverseTally {
    shortLabel: string;
    count: number;
}

export interface CreditorWatchRelatedEntity {
    organisationName: string | null;
    organisationNumber: string | null;
    organisationStatus: string | null;
    // True for a status that is itself an insolvency event — external administration, liquidation,
    // winding up. Distinct from the far more common and far less urgent "Deregistered".
    statusIsAdverse: boolean;
    highRisk: boolean;
    adverseItemCount: number;
    // Counts per adverse type, e.g. "3 insolvency notices", shown without opening the entity.
    tallies: CreditorWatchReportAdverseTally[];
    adverseGroups: CreditorWatchReportAdverseGroup[];
}

export interface CreditorWatchRelatedIndividual {
    fullName: string;
    dateOfBirth: string | null;
    // One entry per officeholder role CreditorWatch lists for this person, e.g. Director, Secretary.
    roles: string[];
    // Derived from the member's share count; CreditorWatch never returns a percentage.
    shareholding: string | null;
    // Entities carrying adverse items, flagged high risk, or in an insolvency status.
    flaggedEntities: CreditorWatchRelatedEntity[];
    // Everything else. Usually the bulk of the list, so the view collapses it by default.
    clearEntities: CreditorWatchRelatedEntity[];
    adverseItemCount: number;
    highRiskEntityCount: number;
}

export interface CreditorWatchReportRiskTotals {
    entityAdverseItemCount: number;
    relatedAdverseItemCount: number;
    highRiskEntityCount: number;
    relatedEntityCount: number;
    clean: boolean;
}

export interface CreditorWatchReadableReport {
    summary: CreditorWatchReadableSummary;
    // Roll-up across the subject entity and every related entity, so the shape of the report is
    // readable before scrolling into it.
    riskTotals: CreditorWatchReportRiskTotals;
    entityAdverseGroups: CreditorWatchReportAdverseGroup[];
    relatedIndividuals: CreditorWatchRelatedIndividual[];
}

export interface CreditorWatchReportData {
    title: string;
    description: string;
    creditReport: {
        atoTaxDefault: any;
        insolvencyNotices: {
            id: string;
            lastmod: string;
            title: string;
            date: string;
            reportedBy: string;
            ruling: string;
            publisher: string;
        }[];
        paymentDefaults: any[];
        mercantileEnquiries: any[];
        courtJudgements: any[];
        loans: any[];
        creditEnquiries: number;
        anzsic: {
            organisationNumber: string;
            divisionCode: string;
            divisionDescription: string;
            anzsicCode: string;
            anzsicDescription: string;
            subdivisionCode: string;
            subdivisionDescription: string;
            groupCode: string;
            groupDescription: string;
        }
    };
    organisationExtract: {
        additionalDetails: string[];
        billing: {
            billingChargeCode: string;
            billingType: string;
        };
        companyAddresses: {
            addressStartDate: string;
            addressEndDate: string | null,
            addressType: string;
            address2: string;
            careOf: string;
            city: string;
            country: string | null,
            detailsType: string;
            documentNumber: string;
            documentNumberQualifier: string | null,
            postcode: string;
            state: string;
            street: string;
        }[];
        companyOfficeHolders: {
            address2: string | null,
            appointmentDate: string;
            ceasedDate: string | null,
            australianBusinessNumber: string | null,
            birthLocation: {
                countryName: string | null,
                iSO3166CountryCode: string | null,
                line1: string | null,
                line2: string | null,
                line3: string | null,
                locality: string;
                postcode: string | null,
                state: string;
                suburb: string | null
            },
            address: {
                countryName: string | null,
                iSO3166CountryCode: string | null,
                line1: string | null,
                line2: string;
                line3: string | null,
                locality: string | null,
                postcode: string;
                state: string;
                suburb: string;
            },
            careOf: string | null,
            detailsType: string;
            documentNumber: string;
            familyName: string;
            givenName1: string;
            givenName2: string | null,
            givenName3: string | null,
            officerBirthDate: string;
            officerName: string | null,
            officerOrganisationNumber: string | null,
            organisationNumberHeading: string | null,
            roleType: string;
            knownInformation: any[];
            isBankruptcy: boolean;
            bankruptcyLastfetched: number;
        }[];
        contactAddresses: {
            addressStartDate: string;
            addressType: string;
            address2: string;
            careOf: string | null,
            city: string;
            country: string | null,
            detailsType: string;
            documentNumber: string | null,
            postcode: string;
            state: string;
            street: string | null,
            addressEndDate: string | null
        }[];
        financialReports: any[];
        header: {
            clientId: string;
            clientReference: string;
            jurisdiction: string;
            messageCreationDate: string;
            messageCreationTime: string;
            messageTraceNumber: string;
            messageType: string;
            version: string;
        };
        organisation: {
            acnc: string | null,
            australianBusinessNumber: string;
            governanceType: string | null,
            organisationName: string;
            organisationNumber: string;
            organisationNumberHeading: string;
            organisationState: string;
            previousStateNumber: string | null,
            registrationDate: string;
            reviewDate: string;
            historicalChanges: {
                dateDeregistered: string | null,
                detailsType: string;
                disclosingEntity: string;
                documentNumber: string;
                documentNumberQualifier: string | null,
                organisationClass: string;
                organisationName: string;
                organisationNameStartDate: string;
                organisationStatus: string;
                organisationSubClass: string;
                organisationType: string;
                reasonDeregistered: string | null,
                schemeCategory1: string | null,
                schemeCategory2: string | null,
                schemeCategory3: string | null,
                schemeCategory4: string | null,
                startDate: string;
                endDate: string | null
            }[]
        };
        shareCapitalGroup: {
            classCode: string;
            classTitle: string;
            detailsType: string;
            documentNumber: string;
            documentNumberQualifier: string | null,
            numberOfShares: number,
            totalAmountDueAndPayable: number,
            totalAmountPaid: number,
        }[];
        shareInterests: {
            beneficiallyOwned: false,
            classCode: string;
            detailsType: string;
            documentNumber: string;
            documentNumberQualifier: string | null,
            fullyPaid: true,
            members: [
                {
                    memberOrganisationNumber: string;
                    organisationNumberHeading: string;
                    memberName: string;
                    address2: string | null,
                    careOf: string | null,
                    city: string;
                    country: string | null,
                    familyName: string | null,
                    givenName1: string | null,
                    givenName2: string | null,
                    jointHolding: false,
                    postcode: string;
                    state: string;
                    street: string;
                    australianBusinessNumber: string | null
                }
            ],
            numberHeld: string;
        }[];
        documentList: any[],
        misc: any[],
        issuedCapital: any[],
        companyDetails: any[],
        
        
    };
    paymentPredictor: {
        paymentPredictorStats: any[],
        paymentPredictorHistory: any[],
        industryPaymentPredictor: {
            atbData: {
                accountAbnHistory: any[],
                othersAbnHistory: {
                    tradeHistory: any[],
                    getSingleHighestCreditExtended: number,
                }
            },
            paymentPredictorPurchased: true,
            paymentPredictorNoEnoughData: false,
            othersAbnHistory: {
                tradeHistory: any[],
                getSingleHighestCreditExtended: number,
            },
            tableOfStats: {
                averageBalance: number,
                averageOverdue: number,
                highestCreditExposureSingleSupplier: number,
                highestCreditExposureCombinedSuppliers: number,
                highestOverdueCreditExposure: number,
                numberOfTradeLines: number,
            },
            overdueDays: number[]
            averageOverdue: [
                {
                    name: string;
                    data: string[],
                    color: string;
                }
            ],
            noGoodData: false,
            displayStateAlert: false
        }
    },
    paymentRating: any;
    industryPaymentRating: {
        currentPaymentRating: {
            asOfDate: string;
            daysOverdue: number,
            rating: string,
            stats: {
                averageBalance: number,
                averageOverdue: number,
                numberOfTradelines: number,
                highestCreditExposureSingleSupplier: number,
                highestCreditExposureCombinedSupplier: number,
                highestCreditExposureOverdue: number,
            }
        },
        paymentRatingHistory: {
            asOfDate: string;
            daysOverdue: number,
            rating: string,
            stats: {
                averageBalance: number,
                averageOverdue: number,
                numberOfTradelines: number,
                highestCreditExposureSingleSupplier: number,
                highestCreditExposureCombinedSupplier: number,
                highestCreditExposureOverdue: number,
            }
        }[]
    }
    currentDirectors: {
        documentNumber: string,
        detailsType: string,
        roleType: string,
        appointmentDate: string,
        familyName: string,
        givenName1: string,
        officerBirthDate: string,
        birthState: string,
        street: string,
        city: string,
        state: string,
        postcode: string,
        acns: string[],
        givenName2: string | null,
        birthLocality: string,
        officerName: string | null,
        officerOrganisationNumber: string | null,
        givenName3: string | null,
        organisationNumberHeading: string | null,
        address2: string | null,
        australianBusinessNumber: string | null,
        currentDirectorships: {
            company: {
                name: string,
                acn: string | null,
                abn: string | null,
                type: string,
                class: string,
                subClass: string,
                status: string,
                registeredDate: string,
            },
            isHighRisk: boolean,
            acn: string,
        }[],
        formerDirectorships: {
            company: {
                name: string,
                acn: string | null,
                abn: string | null,
                type: string,
                class: string,
                subClass: string,
                status: string,
                registeredDate: string,
            },
            isHighRisk: boolean,
            acn: string,
        }[],
    }[];
    companyDetails: any[];
    creditScore: {
        currentCreditScore: number,
        creditScoreHistory: {
            date: string,
            score: number
        }[]
    };
    creditRating: {
        currentCreditRating: string;
        creditRatingHistory: {
            date: string,
            rating: string,
        }[]
    },
    pdfRequestId: string;
    abrData: {
        name: string;
        abn: string;
        acn: string;
        registeredDate: string;
        status: string;
        type: string;
        typeCode: string;
        updatedDate: string;
        description: string;
        lastUpdated: string;
        statusEffectiveFrom: string | null,
        gst: {
            effectiveTo: string | null,
            effectiveFrom: string;
        }[];
        addresses: {
            effectiveTo: string | null,
            effectiveFrom: string;
            postcode: string;
            stateCode: string;
        }[];
        businessNames: {
            effectiveFrom: string;
            name: string;
            type: string;
        }[],
        charityTypes: any[],
        charityEndorsements: any[],
        dgrEndorsements: any[],
        historicalChanges: {
            entityStatus: any[],
            goodsAndServicesTax: any[],
            mainName: any[],
            businessName: string[],
            mainBusinessPhysicalAddress: any[],
            mainTradingName: any[],
            date: string;
        }[]
    },
    statusChange: {
        abrChanges: {
            entityStatus: string;
            date: string;
        }[];
        asicChanges: any[],
        gstChanges: {
            entityStatus: string;
            date: string;
        }[]
        
    },
    asicData: {
        organisation: {
            australianBusinessNumber: string;
            name: string;
            number: string;
            numberType: string;
            type: string;
            status: string;
            address: {
                countryName: string | null,
                iSO3166CountryCode: string | null,
                line1: string | null,
                line2: string | null,
                line3: string | null,
                locality: string | null,
                postcode: string;
                state: string;
                suburb: string;
            },
            controllingJurisdictionExpanded: string;
            controllingJurisdiction: string;
            numberHeading: string;
            registrationDate: string;
            deregistrationDate: string | null,
            reviewDate: string;
            satisfiedChargesIndicator: string;
            class: string;
            subclass:string;
            typeExpanded: string;
            statusExpanded: string;
            classExpanded: string;
            subclassExpanded: string;
            formattedNumber: string;
            acnc: false
        },
        documents: {
            documentQualifier: string | null,
            additionalDescriptions: {
                additionalDescription: string;
                subFormCode: string;
            }[];
            dateProcessed: string;
            dateReceived: string;
            documentNumber: string;
            effectiveDate: string;
            formCode: string;
            numberOfPages: string;
            documentUnderRequisition: string | null
        }[];
        endOfMessage: {
            endOfMessage: string;
            segmentCount: 42
        },
        organisationFormerName: any[];
        ppsr: {
            searchByAbn: {
                nevdisVehicles: any[],
                registrations: any[],
                searchNumber: string | null,
                searchType: string | null
            },
            searchByAcn: {
                nevdisVehicles: any[],
                registrations: any[],
                searchNumber: string | null,
                searchType: string | null
            },
            searchByName: {
                nevdisVehicles: any[],
                registrations: any[],
                searchNumber: string | null,
                searchType: string | null
            }
        }
    }
}

export interface EquifaxConsumerReportData {
    "soapenv:Envelope": {
        "soapenv:Body": {
            "vs:response": {
                "vs:product-header": {
                    "vs:enquiry-id": string;
                    "vs:datetime-requested": string;
                    "vs:datetime-generated": string;
                    "vs:product-name": string;
                    "vs:permission-type": string;
                    "vs:product-data-level": string;
                    "vs:product-version": number;
                };
                "vs:product-data": {
                    "vs:enquiry-report": {
                        "vs:primary-match": {
                            "vs:bureau-reference": number;
                            "vs:individual-name": {
                                "vs:family-name": string;
                                "vs:first-given-name": string;
                            };
                            "vs:gender": string;
                            "vs:date-of-birth": string;
                            "vs:drivers-licence": {
                                "vs:number": string;
                            };
                            "vs:is-deceased": boolean;
                        };
                        "vs:addresses": {
                            "vs:address": {
                                "vs:id-references": {
                                    "vs:id-reference": number;
                                };
                                "vs:street-number": number;
                                "vs:street-name": string;
                                "vs:street-type": string;
                                "vs:suburb": string;
                                "vs:state": string;
                                "vs:postcode": number;
                                "vs:country-code": string;
                            };
                        };
                        "vs:consumer-credit-file": {
                            "vs:credit-enquiries": {
                                "vs:credit-enquiry": {
                                    "vs:id-references": {
                                        "vs:id-reference": number;
                                    };
                                    "vs:account-type": string;
                                    "vs:enquiry-amount": number;
                                    "vs:relationship": string;
                                    "vs:credit-enquirer": {
                                        "vs:name": string;
                                        "vs:classification"?: string;
                                    };
                                }[];
                            };
                            "vs:accounts": {
                                "vs:account": {
                                    "vs:id-references": {
                                        "vs:id-reference": number;
                                    };
                                    "vs:account-id": string;
                                    "vs:original-credit-provider": {
                                        "vs:name": string;
                                        "vs:classification"?: string;
                                    };
                                    "vs:latest-credit-provider": {
                                        "vs:name": string;
                                    };
                                    "vs:account-type": string;
                                    "vs:relationship": string;
                                    "vs:association-information": {
                                        "vs:is-serious-credit-infringement": boolean;
                                    };
                                    "vs:defaults": {
                                        "vs:default": {
                                            "vs:status": string;
                                            "vs:original-default": {
                                                "vs:date-recorded": string;
                                                "vs:default-amount": number;
                                            };
                                            "vs:current-default": {
                                                "vs:date-recorded": string;
                                                "vs:default-amount": number;
                                            };
                                        };
                                    };
                                }[];
                            };
                        };
                        "vs:commercial-credit-file": {
                            "vs:credit-enquiries": {
                                "vs:credit-enquiry": {
                                    "vs:id-references": {
                                        "vs:id-reference": number;
                                    };
                                    "vs:account-type": string;
                                    "vs:enquiry-amount": number;
                                    "vs:relationship": string;
                                    "vs:credit-enquirer": {
                                        "vs:name": string;
                                        "vs:classification"?: string;
                                    };
                                    "vs:enquiry-client-reference"?: string;
                                }[];
                            };
                        };
                        "vs:public-record-or-consumer-credit-information": {
                            "vs:business-names": {
                                "vs:business-name": {
                                    "vs:id-references": {
                                        "vs:id-reference": number;
                                    };
                                    "vs:start-date": string;
                                    "vs:business": {
                                        "vs:bureau-reference": number;
                                        "vs:name": string;
                                        "vs:previous-state-or-territory-registration": {
                                            "vs:state": string;
                                            "vs:number": number;
                                        };
                                        "vs:ABN": number;
                                    };
                                };
                            };
                        };
                    };
                    "vs:summary-data": {
                        "vs:data-block": {
                            "vs:characteristic": {
                                "vs:variable-name": string;
                                "vs:description": string;
                                "vs:value": { 
                                    '#text': string | number;
                                };
                                "vs:data-level": string;
                                "@id": string;
                            }[];
                        }[];
                    };
                    "vs:score-data": {
                        "vs:score": {
                            "vs:scorecard": {
                                "vs:name": string;
                                "vs:version": number;
                                "vs:type": string;
                                "vs:data-level": string;
                            };
                            "vs:risk-odds": number;
                            "vs:key-contributing-factors": {
                                "vs:contributing-factor": {
                                    "vs:name": string;
                                    "vs:description": string;
                                    "vs:score-impactor": string;
                                }[];
                            };
                            "vs:score-masterscale": number;
                        };
                    };
                    "vs:possible-matches": {
                        "vs:possible-match": {
                            "vs:identity": {
                                "vs:bureau-reference": number;
                                "vs:individual-name": {
                                    "vs:family-name": string;
                                    "vs:first-given-name": string;
                                };
                                "vs:gender": string;
                                "vs:date-of-birth": string;
                                "vs:drivers-licence": {
                                    "vs:number": string;
                                };
                            };
                            "vs:addresses": {
                                "vs:address": {
                                    "vs:street-number": number;
                                    "vs:street-name": string;
                                    "vs:street-type": string;
                                    "vs:suburb": string;
                                    "vs:state": string;
                                    "vs:postcode": number;
                                    "vs:country-code": string;
                                };
                            };
                            "vs:employment": {
                                "vs:employer": {
                                    "vs:name": string;
                                };
                            };
                        };
                    };
                };
            }
        }
    }
}

export interface EquifaxPreviousEnquiryPdfResponse {
    "soapenv:Envelope": {
        "soapenv:Header": {
            "wsa:MessageID": string;
            "wsa:To": string;
            "wsa:From": {
                "wsa:Address": string;
            };
            "wsa:Action": string;
        },
        "soapenv:Body": {
            "pe:response": {
                "pe:enquiryId": string;
                "pe:enquiryTime": string;
                "pe:binaryData": string;
            }
        }
    }
}

export interface EquifaxFraudCheckAccessToken {
    access_token: string,
    token_type: 'Bearer',
    expires_in: number,
    scope: string
}

export interface EquifaxFraudCheckReportData {
    metadata: {
        apiVersion: string;
        documentVersion: string;
        status: string;
        productRequestID: string;
        correlationId: string;
        productCode: string;
        productName: string;
        statusDescription: string;
        datetimeRequested: string;
        datetimeGenerated: string;
        fulfillmentId: string;
    };
    requestHeader: {
        clientReference: string;
        operatorId: string;
        operatorName: string;
        entityName: string;
    };
    requestParameters: {
        applicantChannel: string;
        productType: string;
        customerProductName: string;
        productSKU: string;
    };
    requestData: {
        applicant: {
            individualName: {
                surname: string;
                firstName: string;
                middleName: string;
            };
            birthDate: string;
            addresses: Array<{
                addressType: string;
                unitNumber: string;
                streetNumber: string;
                property: string;
                streetName: string;
                streetType: string;
                suburb: string;
                city: string;
                state: string;
                postcode: string;
                countryCode: string;
            }>;
            unformattedAddresses: Array<{
                addressType: string;
                unformattedAddress: string;
            }>;
            documents: Array<{
                documentType: string;
                documentNumber: string;
                countryCode: string | null;
                additionalCardNumber: string;
                state: string | null;
            }>;
            phoneNumbers: Array<{
                phoneType: string;
                isdCountryCode: string;
                areaCode: string;
                phoneNumber: string;
            }>;
            employment: {
                employerName: string;
                countryCode: string;
                employerStatus: string;
            };
            email: string;
        };
    };
    responseData: {
        productData: {
            fraudScore: number;
            applicationAssessment: string;
            applicationMatchedRules: Array<{
                [key: string]: string;
            }>;
            fraudCheckReport: {
                fraudCheckResults: Array<{
                    fraudCheckResultSummary: string;
                    fraudCheckResultDetails: {
                        fraudOccurredDate: string | null;
                        applicationDate: string;
                        reportingSubscriber: string | null;
                        fraudType: string | null;
                        fraudReasonCode: string | null;
                        fraudSubject: string;
                        applicantChannel: string;
                        matchAssessment: string | null;
                        productType: string;
                        customerProductName: string;
                        individualName: {
                            surname: string;
                            firstName: string;
                            middleName: string;
                        };
                        birthDate: string;
                        address: Array<{
                            addressType: string;
                            streetName: string;
                            streetNumber: string;
                            state: string;
                            city: string | null;
                            postcode: string;
                            unitNumber: string;
                            suburb: string;
                            streetType: string;
                            property: string;
                            countryCode: string;
                        }>;
                        documents: Array<{
                            documentType: string;
                            documentNumber: string;
                            countryCode: string | null;
                            additionalCardNumber: string;
                            state: string | null;
                        }>;
                        phoneDetails: Array<{
                            phoneType: string;
                            isdCountryCode: string;
                            areaCode: string;
                            phoneNumber: string;
                        }>;
                        employment: {
                            employerName: string;
                            countryCode: string;
                            employerStatus: string;
                        };
                        email: string;
                        victimFlag: boolean | null;
                        lossAmount: number | null;
                        savingValue: number | null;
                    };
                }>;
            };
            errors: string | null;
        };
    };
}

export interface CreditApprovedDetails {
    creditApprovalDate: string,
    exceptionToPolicy: string,
    creditReference: string,
    dealStatus: string,
    financials: string,
    valuationSource: string,
    approvalConditions: string,
    propertyOwnership: string,
    creditOfficer: string,
    bankStatements: string,
    productType: string,
    companyVeda: number,
    maxDirectorEquifax: number,
    minDirectorEquifax: number,
    dscr: number,
    creditorWatchScore: number,
    abnAge: number,
    gstAge: number,
    abnAgeMoreThan24Months: string,
    adverse: string,
    lvr: number | null,
}

export interface CreditDeclineDetails {
    lostReason: string, 
    comment: string, 
    creditOfficer: string, 
    lostCategory: "Withdrawn" | "Credit Declined",
    withdrawUser?: number,
}

export type CreditorwatchAdverseItemType = 'CourtAction' | 'PaymentDefault';
export type CreditorwatchAdverseItemSource = 'Subject' | 'RelatedEntity';

// One court judgement or payment default from a CreditorWatch report, either on the
// subject entity or on a related entity found via
// organisationExtract.companyOfficeHolders[].knownInformation[].creditReport.
// Persisted as rows in CreditorwatchReportAdverseItem so SQL-type rule items can aggregate.
export interface CreditorwatchAdverseItem {
    itemType: CreditorwatchAdverseItemType;
    source: CreditorwatchAdverseItemSource;
    relatedEntityName: string | null;
    relatedEntityNumber: string | null;
    itemDate: string | null; // YYYY-MM-DD (bureau supplies DD-MM-YYYY)
    amount: number | null; // judgementAmount for court actions, amountOutstanding for defaults
    counterpartyName: string | null; // plaintiff for court actions, posterName for defaults
    settled: boolean | null; // defaultSettled for defaults; null for court actions
}

export interface CreditorwatchExtraction {
    noOfJudgements: number | null;
    mostRecentJudgement: Date | null;
    noOfUnpaidDefaults: number | null;
    mostRecentUnpaidDefault: Date | null;
    totalUnpaidDefault: number | null;
    noOfPaidDefaults: number | null;
    mostRecentPaidDefault: Date | null;
    totalPaidDefault: number | null;
    creditorwatchScore: number | null;
    deregisteredEntity: boolean | null;
    atoDefaults: number | null;
    insolvencyNoticesFromCertainParty: boolean | null;
    unpaidDefault: number | null;
    mercantileEnquiries: number | null;
    courtActions: number | null;
    directorAppointmentDateLessThan12Months: boolean | null;
    rating: string | null;
    crossDirectorshipCourtActionsOrDefaults: boolean | null;
    paidDefaults: number | null;
    currentDirectorshipHighRisk: number | null;
    directorAppointmentDateLessThan24Months: boolean | null;
    adverseItems: CreditorwatchAdverseItem[];
    error: string
}

export interface EquifaxConsumerExtraction {
    bankruptcyFlag: boolean | null;
    error: string;
    financialHardshipFlag: boolean | null;
    directorScore: number | null;
    ageOfEquifaxFile: number | null,
    equifaxDirectorCourtActions: number | null,
    equifaxDirectorPaymentDefaults: number | null,
    equifaxDirectorMercantile: number | null,
    equifaxDirectorCourtWrits: number | null,
    equifaxDirectorPetitions: number | null,
    equifaxConsumerUnpaidDefaultsConsumer: number | null,
    equifaxConsumerUnpaidDefaultsCommercial: number | null,
    equifaxConsumerPaidDefaultsConsumer: number | null,
    equifaxConsumerPaidDefaultsCommercial: number | null,
    equifaxPersonalInsolvenciesCount: number | null,
    equifaxSummons: number | null,
    commercialEnquiries: number | null,
    commercialEnquiriesAfter6Months: number | null,
    consumerCreditEnquiries: number | null,
    rhiIndicatorOnCCR6Months: number | null,
    rhiIndicatorOnCCR12Months: number | null,
    equifaxConsumerPaidDefaultsConsumerSingleObject: number | null,
    equifaxConsumerPaidDefaultsCommercialSingleObject: number | null,
    ccrMortgageDataExists: number | null,
    directorRelatedEntityAdverse: number | null,
    bureauCheckResponseType: string | null,
    individualBureauAge: number | null,
    comprehensiveHighestRepaymentsStatus: number | null,
    comprehensiveHighestRecentRepaymentsStatus: number | null,
    noBureauReport: string | null,
    openAccounts: number | null,
}

export interface EquifaxFraudCheckExtraction {
    error: string;
    fraudMatch: number | null;
}

// adverseItems is excluded: it is persisted as child rows in CreditorwatchReportAdverseItem,
// not as a column on CreditorwatchReport (insert maps every key to a column).
export interface NewCreditorwatchReport extends Omit<CreditorwatchExtraction, 'adverseItems'> {
    abn: string | null;
    acn: string | null;
    creditReportId: number;
    isDeleted: boolean;
}

export interface CreditorwatchReportData extends NewCreditorwatchReport {
    id: number;
    customerId: number;
    created: string;
}

export interface NewEquifaxConsumerReport extends EquifaxConsumerExtraction {
    individualFirstName: string | null;
    individualLastName: string | null;
    individualDoB: Date | string | null;
    creditReportId: number;
    isDeleted: boolean;
}

export interface EquifaxConsumerReportData extends NewEquifaxConsumerReport {
    id: number;
    customerId: number;
    created: string;
}

export interface NewEquifaxFraudCheckReport extends EquifaxFraudCheckExtraction {
    individualFirstName: string | null;
    individualLastName: string | null;
    individualDoB: Date | string | null;
    creditReportId: number;
    isDeleted: boolean;
}

export interface EquifaxFraudCheckReportData extends NewEquifaxFraudCheckReport {
    id: number;
    customerId: number;
    created: string;
}